import React, { useState, useEffect, MutableRefObject } from 'react';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as DragIcon } from '../../../../assests/svg/drop-upload-icon.svg';
import { ReactComponent as ErrorFile } from '../../../../assests/svg/error-file.svg';
import { Typography } from '@mui/material';
import AurButton from '../../../common/aur-button/AurButton';
import { AurTextField } from '../../../common/aur-text-field/AurTextField';
import { ReactComponent as ErrorIcon } from '../../../../assests/svg/error-field-icon.svg';
import AurProgressBar from '../../../common/aur-progressbar/AurProgressBar';
import { t } from 'i18next';
import { uploadOverlay } from '../../../../api/EcaManagementService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';

interface FileUploadWithProgressProps {
  onUploadComplete?: (response: any) => void;
  uploadUrl: string;
  overlayName: string;
  onDuplicateFileError: (hasError: boolean) => void;
  onError?: (errorMessage: string, field: string) => void;
  resetUploadState?: MutableRefObject<(() => void) | null>;
}

const OverlayUpload: React.FC<FileUploadWithProgressProps> = ({
  onUploadComplete,
  overlayName,
  onDuplicateFileError,
  onError,
  resetUploadState,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);
    const [controller, setController] = useState<AbortController | null>(null);

    const handleFileChange = (file: File) => {
        setFile(file);
        setProgress(0);
        setUploadSuccess(false);
        setError(null);
        setIsCanceled(false);
    };

  const resetToInitialState = () => {
    setIsUploading(false);
    setIsCanceled(false);
    setFile(null);
    setError(null);
    };

  useEffect(() => {
    if (resetUploadState) resetUploadState.current = resetToInitialState;

    return () => {
      if (resetUploadState) resetUploadState.current = null;
    };
  }, []);

  useEffect(() => {
    if (file) {
      handleUpload();
    }
  }, [file]);
  const ClientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

    const { projectId: selectedProjectId } =
        useSelector((state: RootState) => state.reports);

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', overlayName);

        const uploadController = new AbortController();
        setController(uploadController);

        try {
            setIsUploading(true);
            setError(null);

            const params = `clientId=${ClientId}&projectId=${selectedProjectId}`;

            await uploadOverlay(params, formData, setProgress, uploadController.signal)
                .then((response) => {
                    if (!isCanceled) {
                        setUploadSuccess(true);
                        onUploadComplete?.(response.data.data.filePath);
                    }
                })
                .catch((err: any) => {
                    if (!isCanceled) {
                        if (err.response?.status === 409) {
                            onDuplicateFileError(true);
                        }
                        const validationErrors = err.response?.data?.validationErrors;
                        if (validationErrors) {
                            validationErrors.forEach((error: { field: string; message: string[] }) => {

                                if (error.field === 'fileName') {
                                    const errorMessage = error.message.join(' ');
                                    onError?.(errorMessage, 'fileName');
                                }

                                else if (error.field === 'file') {
                                    const errorMessage = error.message.join(' ');
                                    onError?.(errorMessage, 'file');
                                    setError('File is required of invalid');
                                }
                            });
                        } else {
                            const generalError = err.response?.data?.message || 'Upload failed. Please try again.';
                            onError?.(generalError, 'gernal');
                        }
                    }

                    setIsUploading(false);
                });
        } catch (err: any) {
            if (!isCanceled) {
                const generalError = err.response?.data?.message || 'Upload failed. Please try again.';
                onError?.(generalError, 'generaleror');
            }
        } finally {
            if (isCanceled || error) {
                setIsUploading(false);
            }

            if (uploadSuccess && progress === 100) {
                setIsUploading(false);
            }
        }
    };

    const handleCancel = () => {
        if (controller) {
            controller.abort();
            setError(null);
            setIsUploading(false);
            setFile(null);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => handleFileChange(acceptedFiles[0]),
        multiple: false,
    });

    return (
        <div className="drop-box-data">
            {file && error ? (
                <>
                    <div>
                        <div className="error-area">
                            <span>
                                {t('overlay.uploadedFile')} <ErrorFile />
                            </span>
                            <AurTextField value={file.name} className="erro-field" ariaLabel={file.name} inputProps={{'aria-readonly': true}} />
                        </div>
                        <Typography tabIndex={0}>
                            <div className="erroMessage">
                                <ErrorIcon /> {t('overlay.DatError')}
                            </div>
                        </Typography>
                    </div>

                    <Typography className="new-file-upload typo-text subhead4_size" tabIndex={0}>
                        {t('overlay.uploadNewDatFile')}
                    </Typography>
                </>
            ) : (
                ''
            )}

            {!isUploading && !isCanceled && (
                <div {...getRootProps()} className="drop-box">
                    <input {...getInputProps()} />
                    <div className="iconsec">
                        <DragIcon aria-label={`${t('overlay.chooseFile')} ${t('overlay.chooseFileButtonHint')}`} />
                        <Typography className="typo-text headline3_size">
                            {t('overlay.dragDropText')}
                        </Typography>
                    </div>
                    <div className="or-text">
                        <Typography>{t('overlay.orText')}</Typography>
                    </div>
                    <div className="upload-button">
                        <AurButton
                            label={t('overlay.chooseFile')}
                            type="submit"
                            classNames={'drawer-close-button'}
                            size="Medium"
                            title={t('overlay.chooseFile')}
                            ariaLabel={`${t('overlay.chooseFile')} ${t('overlay.chooseFileButtonHint')}`}
                        />
                    </div>
                </div>
            )}

            {isUploading && (
                <AurProgressBar value={progress} fileName={file?.name || 'Loading'} showIcon={true}>
                    <AurButton
                        classNames="upload-cancel-button"
                        label={t('buttons.cancelUpload')}
                        type="continue"
                        size="Medium"
                        disabled={progress === 100 || isCanceled}
                        onClick={handleCancel}
                        ariaLabel={`${t('buttons.cancelUpload')} ${t('overlay.stopUploadButtonHint')}`}
                    />
                </AurProgressBar>
            )}
        </div>
    );
};

export default OverlayUpload;
